import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import ThumbnailActions from "Redux/ThumbnailRedux";
import Spinner from "../Spinner";

// Helper to get aspect ratio container styles
const getAspectRatioStyles = (aspectRatio) => {
  switch (aspectRatio) {
    case "9:16":
      // Vertical: constrain height and center
      return {
        containerClass: "aspect-[9/16] max-h-[50vh]",
        wrapperClass: "flex justify-center",
      };
    case "1:1":
      // Square: constrain to reasonable size
      return {
        containerClass: "aspect-square max-h-[40vh]",
        wrapperClass: "flex justify-center",
      };
    case "16:9":
    default:
      // Landscape: full width
      return {
        containerClass: "aspect-video w-full",
        wrapperClass: "",
      };
  }
};

export default function SceneReviewStep({
  scenes = [],
  audioUrl = "",
  aspectRatio = "16:9",
  onScenesChange,
  onConfirm,
  onBack,
  isConfirming = false,
}) {
  const dispatch = useDispatch();
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(0);
  const [editedPrompts, setEditedPrompts] = useState({});
  const [editedAnimationPrompts, setEditedAnimationPrompts] = useState({});
  const [regeneratingScenes, setRegeneratingScenes] = useState(new Set());
  const [animatingScenes, setAnimatingScenes] = useState(new Set());
  const [errors, setErrors] = useState({});
  const [previewMode, setPreviewMode] = useState("image"); // "image" or "video"
  const [playingSceneIndex, setPlayingSceneIndex] = useState(null);
  
  const audioRef = useRef(null);

  // Initialize edited prompts from scenes
  useEffect(() => {
    const prompts = {};
    const animPrompts = {};
    scenes.forEach((scene, index) => {
      prompts[index] = scene.prompt || "";
      animPrompts[index] = scene.animation_prompt || "";
    });
    setEditedPrompts(prompts);
    setEditedAnimationPrompts(animPrompts);
  }, [scenes]);

  // Reset preview mode when scene changes or when video becomes available
  useEffect(() => {
    const scene = scenes[selectedSceneIndex];
    if (scene?.animated_video_url) {
      setPreviewMode("video");
    } else {
      setPreviewMode("image");
    }
  }, [selectedSceneIndex, scenes]);

  // Stop audio when component unmounts or audioUrl changes
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]);

  // Play audio for a specific scene's timestamp
  const handlePlaySceneAudio = (sceneIndex, e) => {
    if (e) e.stopPropagation();
    
    if (!audioUrl) return;
    
    const scene = scenes[sceneIndex];
    if (!scene) return;
    
    // If already playing this scene, stop it
    if (playingSceneIndex === sceneIndex && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlayingSceneIndex(null);
      return;
    }
    
    // Create or reuse audio element
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
    } else if (audioRef.current.src !== audioUrl) {
      audioRef.current.src = audioUrl;
    }
    
    const audio = audioRef.current;
    const startTime = scene.startTime || 0;
    const endTime = scene.endTime || startTime + 5;
    
    // Set up end time handler
    const handleTimeUpdate = () => {
      if (audio.currentTime >= endTime) {
        audio.pause();
        setPlayingSceneIndex(null);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
    
    const handleEnded = () => {
      setPlayingSceneIndex(null);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
    
    // Remove previous listeners
    audio.removeEventListener('timeupdate', handleTimeUpdate);
    audio.removeEventListener('ended', handleEnded);
    
    // Add new listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    
    // Seek to start time and play
    audio.currentTime = startTime;
    audio.play().then(() => {
      setPlayingSceneIndex(sceneIndex);
    }).catch(err => {
      console.error('Failed to play audio:', err);
      setPlayingSceneIndex(null);
    });
  };

  const selectedScene = scenes[selectedSceneIndex];

  // Get status badge for a scene
  const getStatusBadge = (scene, isRegenerating = false) => {
    if (isRegenerating) {
      return { label: "Generating", color: "bg-yellow-100 text-yellow-700", icon: true };
    }
    if (scene.status === "failed" || (!scene.image_url && !scene.status)) {
      return { label: "Failed", color: "bg-red-100 text-red-700", icon: "✕" };
    }
    if (scene.animation_status === "completed") {
      return { label: "Animated", color: "bg-purple-100 text-purple-700" };
    }
    if (scene.animation_status === "processing") {
      return { label: "Animating...", color: "bg-yellow-100 text-yellow-700" };
    }
    if (scene.animation_status === "failed") {
      return { label: "Animation Failed", color: "bg-red-100 text-red-700" };
    }
    if (!scene.image_url) {
      return { label: "Pending", color: "bg-gray-100 text-gray-600" };
    }
    return { label: "Generated", color: "bg-green-100 text-green-700" };
  };

  // Check if a scene is failed
  const isSceneFailed = (scene) => {
    return scene.status === "failed" || (!scene.image_url && !scene.status);
  };

  // Format time range
  const formatTimeRange = (startTime, endTime) => {
    const formatTime = (t) => `${Math.floor(t)}s`;
    return `${formatTime(startTime)}-${formatTime(endTime)}`;
  };

  // Truncate text
  const truncateText = (text, maxLength = 60) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // Handle prompt change
  const handlePromptChange = (value) => {
    setEditedPrompts((prev) => ({
      ...prev,
      [selectedSceneIndex]: value,
    }));
  };

  // Handle animation prompt change
  const handleAnimationPromptChange = (value) => {
    setEditedAnimationPrompts((prev) => ({
      ...prev,
      [selectedSceneIndex]: value,
    }));
  };

  // Reset prompt to original
  const handleResetPrompt = () => {
    if (selectedScene) {
      setEditedPrompts((prev) => ({
        ...prev,
        [selectedSceneIndex]: selectedScene.prompt || "",
      }));
    }
  };

  // Reset animation prompt to original
  const handleResetAnimationPrompt = () => {
    if (selectedScene) {
      setEditedAnimationPrompts((prev) => ({
        ...prev,
        [selectedSceneIndex]: selectedScene.animation_prompt || "",
      }));
    }
  };

  // Regenerate image for selected scene
  const handleRegenerateImage = () => {
    const thumbnailId = selectedScene?.thumbnail_image_id || selectedScene?.id;
    if (!thumbnailId || regeneratingScenes.has(selectedSceneIndex)) return;

    const newPrompt = editedPrompts[selectedSceneIndex] || selectedScene.prompt;

    setRegeneratingScenes((prev) => new Set([...prev, selectedSceneIndex]));
    setErrors((prev) => ({ ...prev, [selectedSceneIndex]: null }));

    dispatch(ThumbnailActions.thumbnailRegenerateRequest(thumbnailId, newPrompt, (regenData, regenError) => {
      if (regenError) {
        console.error("Failed to regenerate image:", regenError);
        setErrors((prev) => ({
          ...prev,
          [selectedSceneIndex]: regenError || "Failed to regenerate image",
        }));
        setRegeneratingScenes((prev) => {
          const next = new Set(prev);
          next.delete(selectedSceneIndex);
          return next;
        });
        return;
      }

      // Poll for completion
      dispatch(ThumbnailActions.thumbnailPollRequest(thumbnailId, 3000, (result, pollError) => {
        setRegeneratingScenes((prev) => {
          const next = new Set(prev);
          next.delete(selectedSceneIndex);
          return next;
        });

        if (pollError) {
          console.error("Failed to regenerate image:", pollError);
          setErrors((prev) => ({
            ...prev,
            [selectedSceneIndex]: pollError || "Failed to regenerate image",
          }));
          return;
        }

        const updatedScenes = scenes.map((scene, index) =>
          index === selectedSceneIndex
            ? { ...scene, image_url: result.image_url, prompt: result.prompt, status: "completed", error_message: null }
            : scene
        );
        onScenesChange?.(updatedScenes);

        setEditedPrompts((prev) => ({
          ...prev,
          [selectedSceneIndex]: result.prompt,
        }));
      }));
    }));
  };

  // Toggle animation for selected scene
  const handleToggleAnimation = () => {
    if (!selectedScene) return;

    const updatedScenes = scenes.map((scene, index) =>
      index === selectedSceneIndex
        ? { ...scene, animate_enabled: !scene.animate_enabled }
        : scene
    );
    onScenesChange?.(updatedScenes);
  };

  // Animate the selected scene
  const handleAnimateScene = () => {
    const thumbnailId = selectedScene?.thumbnail_image_id || selectedScene?.id;
    if (!thumbnailId || animatingScenes.has(selectedSceneIndex)) return;

    const animationPrompt = editedAnimationPrompts[selectedSceneIndex] || selectedScene.animation_prompt;
    // Force re-animation if scene already has an animation
    const forceReanimate = selectedScene.animation_status === "completed" || !!selectedScene.animated_video_url;

    setAnimatingScenes((prev) => new Set([...prev, selectedSceneIndex]));
    setErrors((prev) => ({ ...prev, [selectedSceneIndex]: null }));

    dispatch(ThumbnailActions.animationGenerateRequest(thumbnailId, animationPrompt, forceReanimate, (animData, animError) => {
      if (animError) {
        console.error("Failed to animate scene:", animError);
        setErrors((prev) => ({
          ...prev,
          [selectedSceneIndex]: animError || "Failed to animate scene",
        }));
        setAnimatingScenes((prev) => {
          const next = new Set(prev);
          next.delete(selectedSceneIndex);
          return next;
        });
        return;
      }

      // Poll for completion
      dispatch(ThumbnailActions.animationPollRequest(thumbnailId, 3000, (result, pollError) => {
        setAnimatingScenes((prev) => {
          const next = new Set(prev);
          next.delete(selectedSceneIndex);
          return next;
        });

        if (pollError) {
          console.error("Failed to animate scene:", pollError);
          setErrors((prev) => ({
            ...prev,
            [selectedSceneIndex]: pollError || "Failed to animate scene",
          }));
          return;
        }

        const updatedScenes = scenes.map((scene, index) =>
          index === selectedSceneIndex
            ? { 
                ...scene, 
                animated_video_url: result.animated_video_url,
                animation_status: result.animation_status,
                animation_prompt: result.animation_prompt,
                animate_enabled: true,
              }
            : scene
        );
        onScenesChange?.(updatedScenes);

        setEditedAnimationPrompts((prev) => ({
          ...prev,
          [selectedSceneIndex]: result.animation_prompt,
        }));
        
        setPreviewMode("video");
      }));
    }));
  };

  // Animate all scenes
  const handleAnimateAll = () => {
    const indicesToAnimate = scenes
      .map((scene, index) => ({ scene, index }))
      .filter(({ scene, index }) => {
        const thumbnailId = scene.thumbnail_image_id || scene.id;
        return thumbnailId && scene.image_url && !animatingScenes.has(index) && scene.animation_status !== "completed";
      })
      .map(({ index }) => index);

    if (indicesToAnimate.length === 0) return;

    setAnimatingScenes((prev) => new Set([...prev, ...indicesToAnimate]));

    let completedCount = 0;
    const results = [];
    const updatedScenes = [...scenes];

    // Helper to check if all animations are done
    const checkAllComplete = () => {
      completedCount++;
      if (completedCount === indicesToAnimate.length) {
        // All animations finished, update state
        results.forEach(({ index, success, result, error }) => {
          if (success && result) {
            updatedScenes[index] = {
              ...updatedScenes[index],
              animated_video_url: result.animated_video_url,
              animation_status: result.animation_status,
              animation_prompt: result.animation_prompt,
              animate_enabled: true,
            };
            setEditedAnimationPrompts((prev) => ({
              ...prev,
              [index]: result.animation_prompt,
            }));
          } else if (error) {
            setErrors((prev) => ({
              ...prev,
              [index]: error,
            }));
          }
        });

        onScenesChange?.(updatedScenes);
        setAnimatingScenes(new Set());
      }
    };

    // Animate each scene
    indicesToAnimate.forEach((index) => {
      const scene = scenes[index];
      const thumbnailId = scene.thumbnail_image_id || scene.id;
      const animationPrompt = editedAnimationPrompts[index] || scene.animation_prompt;
      // Force re-animation if scene already has an animation
      const forceReanimate = scene.animation_status === "completed" || !!scene.animated_video_url;

      dispatch(ThumbnailActions.animationGenerateRequest(thumbnailId, animationPrompt, forceReanimate, (animData, animError) => {
        if (animError) {
          console.error(`Failed to animate scene ${index + 1}:`, animError);
          results.push({ index, success: false, error: animError });
          checkAllComplete();
          return;
        }

        dispatch(ThumbnailActions.animationPollRequest(thumbnailId, 3000, (result, pollError) => {
          if (pollError) {
            results.push({ index, success: false, error: pollError });
          } else {
            results.push({ index, success: true, result });
          }
          checkAllComplete();
        }));
      }));
    });
  };

  const hasGeneratingScenes = regeneratingScenes.size > 0;
  const hasAnimatingScenes = animatingScenes.size > 0;
  const failedScenesCount = scenes.filter((scene) => isSceneFailed(scene)).length;
  const allScenesReady = scenes.every((scene) => scene.image_url);
  const allScenesAnimated = scenes.every((scene) => scene.animation_status === "completed");
  const scenesNeedingAnimation = scenes.filter((scene) => scene.image_url && scene.animation_status !== "completed").length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-28 sm:pb-24">
        {/* Back button */}
        <button
          onClick={onBack}
          disabled={isConfirming}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors mb-6 disabled:opacity-50"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Script
        </button>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Panel - Scene List (horizontal scroll on mobile, sidebar on desktop) */}
          <div className="lg:w-80 lg:flex-shrink-0">
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Scenes</h2>
                {scenesNeedingAnimation > 0 && (
                  <button
                    onClick={handleAnimateAll}
                    disabled={hasAnimatingScenes || !allScenesReady}
                    className="px-3 py-1.5 bg-purple-500 text-white text-xs font-medium rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {hasAnimatingScenes ? (
                      <>
                        <Spinner size="xs" color="text-white" />
                        Animating...
                      </>
                    ) : (
                      <>
                        <span>✨</span>
                        Animate All ({scenesNeedingAnimation} cr)
                      </>
                    )}
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-500">Review and edit each scene before animation</p>
            </div>

            <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
              {scenes.map((scene, index) => {
                const isSelected = index === selectedSceneIndex;
                const isRegenerating = regeneratingScenes.has(index);
                const status = getStatusBadge(scene, isRegenerating);

                return (
                  <div
                    key={scene.id || index}
                    onClick={() => setSelectedSceneIndex(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedSceneIndex(index)}
                    className={`flex-shrink-0 w-64 lg:w-full text-left rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected
                        ? "border-primary-500 bg-white shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex gap-3">
                        {/* Thumbnail */}
                        <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                          {isRegenerating ? (
                            <div className="w-full h-full flex items-center justify-center bg-yellow-50">
                              <Spinner size="md" color="text-yellow-500" />
                            </div>
                          ) : scene.image_url ? (
                            <img
                              src={scene.image_url}
                              alt={`Scene ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : isSceneFailed(scene) ? (
                            <div className="w-full h-full flex items-center justify-center bg-red-50">
                              <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-semibold text-gray-900">Scene {index + 1}</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                              {status.icon && <Spinner size="xs" color="text-yellow-700" />}
                              {status.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                            <span>{formatTimeRange(scene.startTime || 0, scene.endTime || 0)}</span>
                            {audioUrl && (
                              <button
                                onClick={(e) => handlePlaySceneAudio(index, e)}
                                className={`p-0.5 rounded hover:bg-gray-100 transition-colors ${
                                  playingSceneIndex === index ? "text-primary-500" : "text-gray-400 hover:text-gray-600"
                                }`}
                                title={playingSceneIndex === index ? "Stop audio" : "Play audio for this scene"}
                              >
                                {playingSceneIndex === index ? (
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {truncateText(scene.script_text || scene.narration || "", 80)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel - Scene Editor */}
          <div className="flex-1">
            {selectedScene ? (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Scene {selectedSceneIndex + 1} Editor</h3>
                      <p className="text-sm text-gray-500">Review and customize this scene before animation</p>
                    </div>
                    {audioUrl && (
                      <button
                        onClick={() => handlePlaySceneAudio(selectedSceneIndex)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                          playingSceneIndex === selectedSceneIndex
                            ? "bg-primary-100 text-primary-700 hover:bg-primary-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {playingSceneIndex === selectedSceneIndex ? (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="6" y="4" width="4" height="16" rx="1" />
                              <rect x="14" y="4" width="4" height="16" rx="1" />
                            </svg>
                            Stop Audio
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                            Play Audio ({formatTimeRange(selectedScene.startTime || 0, selectedScene.endTime || 0)})
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  {/* Preview Section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Preview</h4>
                      {/* Preview Toggle - only show if video is available */}
                      {selectedScene.animated_video_url && (
                        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                          <button
                            onClick={() => setPreviewMode("image")}
                            className={`px-3 py-1 text-xs font-medium transition-colors ${
                              previewMode === "image"
                                ? "bg-primary-500 text-white"
                                : "bg-white text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            Image
                          </button>
                          <button
                            onClick={() => setPreviewMode("video")}
                            className={`px-3 py-1 text-xs font-medium transition-colors ${
                              previewMode === "video"
                                ? "bg-primary-500 text-white"
                                : "bg-white text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            Video
                          </button>
                        </div>
                      )}
                    </div>
                    <div className={getAspectRatioStyles(aspectRatio).wrapperClass}>
                    <div className={`relative  bg-black rounded-xl overflow-hidden`}>
                      {regeneratingScenes.has(selectedSceneIndex) ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
                          <Spinner size="xl" className="mb-2" />
                          <p className="text-sm text-gray-500">Generating image...</p>
                        </div>
                      ) : animatingScenes.has(selectedSceneIndex) ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-purple-50">
                          <Spinner size="xl" color="text-purple-500" className="mb-2" />
                          <p className="text-sm text-purple-600">Animating scene...</p>
                        </div>
                      ) : previewMode === "video" && selectedScene.animated_video_url ? (
                        <>
                          <video
                            src={selectedScene.animated_video_url}
                            className="mx-auto"
                            controls
                            autoPlay
                            loop
                            muted
                          />
                          <div className="absolute top-3 right-3 px-2 py-1 bg-purple-500/80 text-white text-xs rounded flex items-center gap-1">
                            <span>✨</span> Animated
                          </div>
                        </>
                      ) : selectedScene.image_url ? (
                        <>
                          <img
                            src={selectedScene.image_url}
                            alt={`Scene ${selectedSceneIndex + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 text-white text-xs rounded">
                            {aspectRatio}
                          </div>
                        </>
                      ) : isSceneFailed(selectedScene) ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50">
                          <svg className="w-10 h-10 mb-2 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <p className="text-sm font-medium text-red-600">Image generation failed</p>
                          {selectedScene.error_message && (
                            <p className="text-xs text-red-500 mt-1 px-4 text-center">{selectedScene.error_message}</p>
                          )}
                          <p className="text-xs text-red-400 mt-2">Edit the prompt below and click Regenerate</p>
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                          <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm">No image generated</p>
                        </div>
                      )}
                    </div>
                    </div>
                  </div>

                  {/* Error message */}
                  {errors[selectedSceneIndex] && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-2">
                      <p className="text-sm text-red-600">{errors[selectedSceneIndex]}</p>
                      <button
                        onClick={() => setErrors((prev) => ({ ...prev, [selectedSceneIndex]: null }))}
                        className="text-red-400 hover:text-red-600 flex-shrink-0 p-0.5"
                        aria-label="Dismiss error"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Image Prompt Section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Image Prompt</h4>
                      <button
                        onClick={handleResetPrompt}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Reset
                      </button>
                    </div>
                    <textarea
                      value={editedPrompts[selectedSceneIndex] || ""}
                      onChange={(e) => handlePromptChange(e.target.value)}
                      placeholder="Enter image prompt..."
                      rows={4}
                      className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                    <button
                      onClick={handleRegenerateImage}
                      disabled={regeneratingScenes.has(selectedSceneIndex)}
                      className="mt-2 w-full px-4 py-2.5 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {regeneratingScenes.has(selectedSceneIndex) ? (
                        <>
                          <Spinner size="xs" color="text-white" />
                          Generating...
                        </>
                      ) : isSceneFailed(selectedScene) ? (
                        <>
                          <span>↻</span>
                          Retry Image Generation
                        </>
                      ) : (
                        <>
                          <span>↻</span>
                          Regenerate Image
                        </>
                      )}
                    </button>
                  </div>

                  {/* Animation Prompt Section */}
                  <div className="mb-6 p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-purple-500">✨</span>
                        <h4 className="text-sm font-medium text-gray-700">Animation Prompt</h4>
                        <span className="text-xs text-purple-500 bg-purple-100 px-1.5 py-0.5 rounded">1 credit</span>
                      </div>
                      <button
                        onClick={handleResetAnimationPrompt}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Reset
                      </button>
                    </div>
                    <textarea
                      value={editedAnimationPrompts[selectedSceneIndex] || ""}
                      onChange={(e) => handleAnimationPromptChange(e.target.value)}
                      placeholder="Describe how the image should be animated..."
                      rows={4}
                      className="w-full px-3 py-2 text-base border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none bg-white"
                    />
                    <button
                      onClick={handleAnimateScene}
                      disabled={animatingScenes.has(selectedSceneIndex) || !selectedScene.image_url}
                      className="mt-2 w-full px-4 py-2.5 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {animatingScenes.has(selectedSceneIndex) ? (
                        <>
                          <Spinner size="xs" color="text-white" />
                          Animating...
                        </>
                      ) : selectedScene.animated_video_url ? (
                        <>
                          <span>✨</span>
                          Re-animate Scene
                        </>
                      ) : (
                        <>
                          <span>✨</span>
                          Animate Scene
                        </>
                      )}
                    </button>
                    {selectedScene.animation_status === "failed" && selectedScene.animation_error_message && (
                      <p className="mt-2 text-xs text-red-600">{selectedScene.animation_error_message}</p>
                    )}
                  </div>

                  {/* Animation Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-700">Use animation in video</span>
                        <span className="text-gray-400 cursor-help" title="Include animated version in final video">ⓘ</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {selectedScene.animated_video_url 
                          ? "Animation available - toggle to use in final video" 
                          : "Animate scene first to enable this option"}
                      </p>
                    </div>
                    <button
                      onClick={handleToggleAnimation}
                      disabled={!selectedScene.animated_video_url}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                        selectedScene.animate_enabled ? "bg-primary-500" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          selectedScene.animate_enabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <p className="text-gray-500">Select a scene to view details</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            {failedScenesCount > 0 ? (
              <span className="flex items-center gap-1.5 text-red-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                {failedScenesCount} scene{failedScenesCount > 1 ? "s" : ""} failed — retry from the scene editor
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Review all scenes before continuing
              </span>
            )}
          </div>
          <button
            onClick={onConfirm}
            disabled={!allScenesReady || hasGeneratingScenes || isConfirming}
            className="px-8 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
          >
            {isConfirming ? (
              <>
                <Spinner size="sm" color="text-white" />
                Processing...
              </>
            ) : (
              <>
                Continue to Preview
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
